import { Component } from '@theme/component';
import { mediaQueryLarge } from '@theme/utilities';

/**
 * Background videos currently playing. Used to cap concurrency at one on mobile,
 * where decoding several background videos at once is a common source of jank.
 * @type {Set<VideoBackgroundComponent>}
 */
const playingBackgroundVideos = new Set();

/**
 * A custom element that renders a video background.
 *
 * Playback is viewport-gated (mirrors `AutoplayVideoManager` in `media.js`, which does the
 * same for `<deferred-media>` videos): the video only plays while the component is in or
 * near the viewport, and on mobile only one background video plays at a time. Offscreen
 * videos stop costing CPU/GPU/battery instead of running from page load.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  /** @type {IntersectionObserver | null} */
  #observer = null;

  connectedCallback() {
    super.connectedCallback();

    const { videoSources, videoElement } = this.refs;

    for (const source of videoSources) {
      const { videoSource } = source.dataset;

      if (videoSource) source.setAttribute('src', videoSource);
    }

    videoElement.load();

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.#play();
          } else {
            this.#pause();
          }
        }
      },
      { rootMargin: '100px' }
    );

    this.#observer.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#observer?.disconnect();
    this.#observer = null;
    this.#pause();
  }

  #play() {
    const { videoElement } = this.refs;

    // Only one background video may play at a time on mobile.
    if (!mediaQueryLarge.matches) {
      for (const other of playingBackgroundVideos) {
        if (other !== this) other.#pause();
      }
    }

    const playback = videoElement.play();
    if (playback && typeof playback.catch === 'function') playback.catch(() => {});
    playingBackgroundVideos.add(this);
  }

  #pause() {
    this.refs.videoElement?.pause();
    playingBackgroundVideos.delete(this);
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
